import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Step, StepContent, StepLabel, Stepper,
} from '@mui/material';
import NovoAcordoPassoUm from './PassoUm/NovoAcordoPassoUm';
import NovoAcordoPassoDois from './PassoDois/NovoAcordoPassoDois';
import NovoAcordoPassoTres from './Passo3/NovoAcordoPassoTres';

/**
 * Stepper do fluxo Novo Acordo — port de `NovoAcordo.jsx` real.
 *
 * Passo 1 (Etapa 4), Passo 2 (Etapa 5) e Passo 3 (Etapa 6) do plano AW2-1032
 * já são reais.
 *
 * `BotaoNovoAcordo` (Etapa 3) preserva `aberto` por processo via `useRef` —
 * reabrir o painel para um processo B enquanto ele já estava marcado como
 * aberto para B não remonta este componente, só troca a prop `idProcesso`.
 * Todo estado derivado do processo anterior precisa resetar aqui (mesmo
 * padrão já documentado em `src/prototipo/CLAUDE.md` para `AcaoRequisicaoProcesso.jsx`/
 * `BarraAcoesProcesso.jsx`) — sem isso, `selectedIds` do processo A vazaria
 * para o payload de `findResumoAcordo` do processo B.
 *
 * `useLayoutEffect` (não `useEffect`) de propósito: o reset precisa commitar
 * antes de qualquer pintura/interação do usuário com o novo `idProcesso` —
 * um `useEffect` comum deixaria uma janela (por menor que seja) em que
 * `listaDeIds` ainda refletiria a seleção do processo anterior.
 *
 * Sem `CardWrapper` próprio (Etapa 9) — este componente agora é o conteúdo de
 * um `DialogContent` montado por `BotaoNovoAcordo.jsx`, que já fornece o
 * título e o contêiner visual.
 */
export default function NovoAcordo({ idProcesso, onFechar, onAcordoGerado }) {
  const [passoAtivo, setPassoAtivo] = useState(0);
  const [selectedIds, setSelectedIds] = useState({});
  const [dadosDefault, setDadosDefault] = useState({});
  const [dadosResumo, setDadosResumo] = useState({});
  const [parcelas, setParcelas] = useState([]);
  const [dataFinalPasso3, setDataFinalPasso3] = useState('');
  const [dadosPadraoForm, setDadosPadraoForm] = useState({});

  useLayoutEffect(() => {
    setPassoAtivo(0);
    setSelectedIds({});
    setDadosDefault({});
    setDadosResumo({});
    setParcelas([]);
    setDataFinalPasso3('');
    setDadosPadraoForm({});
  }, [idProcesso]);

  const listaDeIds = Object.keys(selectedIds)
    .filter(id => selectedIds[id])
    .map(Number);

  const avancarPasso = () => setPassoAtivo(atual => atual + 1);
  const voltarPasso = () => setPassoAtivo(atual => atual - 1);

  /**
   * "Voltar" a partir do Passo 3 restaura em `dadosDefault` os valores que o
   * usuário havia confirmado no Passo 2 (`dadosPadraoForm`, capturado ao
   * clicar "Próximo") — sem isso, remontar `NovoAcordoPassoDois` (StepContent
   * desmonta/remonta por `passoAtivo`) recalcularia o formulário a partir dos
   * defaults originais do servidor, descartando os ajustes do usuário. Port
   * simplificado de `onClickVoltarPasso` real (que usa um `dadosFormFields`
   * próprio, não implementado nesta etapa — `dadosPadraoForm` é o snapshot
   * mais recente disponível).
   */
  const voltarParaPasso2 = () => {
    setParcelas([]);
    setDadosDefault(atual => ({ ...atual, ...dadosPadraoForm }));
    setPassoAtivo(atual => atual - 1);
  };

  return (
    <Stepper activeStep={passoAtivo} orientation="vertical">
      <Step>
        <StepLabel>PASSO 1</StepLabel>
        <StepContent>
          {passoAtivo === 0 && (
          <NovoAcordoPassoUm
            idProcesso={idProcesso}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            listaDeIds={listaDeIds}
            setDadosDefault={setDadosDefault}
            setDadosResumo={setDadosResumo}
            onAvancar={avancarPasso}
            onVoltar={onFechar}
          />
          )}
        </StepContent>
      </Step>
      <Step>
        <StepLabel>PASSO 2</StepLabel>
        <StepContent>
          {passoAtivo === 1 && (
          <NovoAcordoPassoDois
            idProcesso={idProcesso}
            listaDeIds={listaDeIds}
            dadosDefault={dadosDefault}
            dadosResumo={dadosResumo}
            setDadosResumo={setDadosResumo}
            setParcelas={setParcelas}
            setDataFinalPasso3={setDataFinalPasso3}
            setDadosPadraoForm={setDadosPadraoForm}
            onVoltar={voltarPasso}
            onAvancar={avancarPasso}
          />
          )}
        </StepContent>
      </Step>
      <Step>
        <StepLabel>PASSO 3</StepLabel>
        <StepContent>
          {passoAtivo === 2 && (
          <NovoAcordoPassoTres
            idProcesso={idProcesso}
            dadosResumo={dadosResumo}
            parcelas={parcelas}
            setParcelas={setParcelas}
            dadosPadraoForm={dadosPadraoForm}
            dataFinalPasso3={dataFinalPasso3}
            onVoltar={voltarParaPasso2}
            onFechar={onFechar}
            onAcordoGerado={onAcordoGerado}
          />
          )}
        </StepContent>
      </Step>
    </Stepper>
  );
}

NovoAcordo.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onFechar: PropTypes.func.isRequired,
  onAcordoGerado: PropTypes.func,
};

NovoAcordo.defaultProps = {
  idProcesso: null,
  onAcordoGerado: null,
};
